// Mirrors reporting-hub's PreflightPage Tab 1 "Select Data". Two ways in:
// - manual: POST .../reportings/create {source: 'manual_upload'}, then the
//   loan tape (+ optional CashFlows) go browser -> S3 via uploadFile()
//   (presign -> PUT -> complete);
// - database: POST .../reportings/create {}, then a `db_fetch` job.
// Neither runs the checks any more - once a loan tape is in, this tab shows
// the recap, the 9 platform file slots, and a "Validate Data" button (the
// `validate` job: preflight + every check + everything the Validation tab
// shows). `status.inputs_summary` decides between upload form and recap, so
// a reload with stage 'created' but a loan tape already uploaded still lands
// on the recap. Replacing the LoanTape/CashFlows file of an existing
// reporting isn't wired up (see the note rendered below).

import React, { useEffect, useState } from 'react';
import axiosWithAuth from '../../../helpers/AxiosHelper';
import { FileUpload } from 'primereact/fileupload';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Tag } from 'primereact/tag';
import PlatformFilesCard from '../validation/PlatformFilesCard';
import LoadingState from '../../common/LoadingState';
import { API, apiErrorDetail, uploadFile, useWizardJob } from '../jobs';
import { dateToIso } from '../calculation/calcFormat';
import errorMessage from '../../../helpers/errorMessage';

// Icon tiles use the brand tokens (not a per-card accent color) so both
// cards read the same in light and dark mode.
const SectionCard = ({ icon, title, children }) => (
  <div className="cr-card cr-select-card">
    <div className="cr-card-header">
      <span className="cr-icon-tile" aria-hidden="true">
        <i className={`pi ${icon}`} />
      </span>
      <h4 className="cr-card-title">{title}</h4>
    </div>
    {children}
  </div>
);

const FileInfoCard = ({ icon, label, file }) => (
  <div className="cr-card cr-file-info-card">
    <span className="cr-icon-tile cr-icon-tile-lg" aria-hidden="true">
      <i className={`pi ${icon}`} />
    </span>
    <div className="cr-file-info-body">
      <div className="cr-eyebrow">{label}</div>
      <div className="cr-file-info-name" title={file.filename}>
        {file.filename}
      </div>
      <div className="flex flex-wrap gap-2 mt-2">
        {file.rows != null && file.columns ? (
          <>
            <Tag severity="secondary" className="cr-tag-muted" value={`${file.rows.toLocaleString()} rows`} />
            <Tag severity="secondary" className="cr-tag-muted" value={`${file.columns.length} columns`} />
          </>
        ) : (
          <Tag severity="warning" value="Not validated yet" />
        )}
      </div>
    </div>
  </div>
);

const SelectDataTab = ({ reportingId, status, onReportingCreated, onValidated, onStatusChanged, setLoading }) => {
  const [loantapeFile, setLoantapeFile] = useState(null);
  const [cashflowsFile, setCashflowsFile] = useState(null);
  const [error, setError] = useState(null);
  const [validateError, setValidateError] = useState(null);
  const [loanbookLastDate, setLoanbookLastDate] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [inputsRecap, setInputsRecap] = useState(null);
  const { runJob, activeJob, busy } = useWizardJob();

  const inputsSummary = (status && status.inputs_summary) || {};
  const hasPreflight = !!(status && status.stage && status.stage !== 'created');
  const hasLoantape = hasPreflight || !!inputsSummary.loantape;
  const validatedAt = status && status.validation ? status.validation.validated_at : null;
  const validationStale = !!(status && status.validation && status.validation.stale);

  useEffect(() => {
    if (!reportingId || !hasPreflight) {
      setInputsRecap(null);
      return;
    }
    axiosWithAuth
      .get(`${API}/reportings/${reportingId}/preflight`)
      .then((res) => setInputsRecap(res.data.inputs))
      .catch(() => setInputsRecap(null));
  }, [reportingId, hasPreflight, validatedAt]);

  const ensureReporting = async (body) => {
    if (reportingId) return reportingId;
    const res = await axiosWithAuth.post(`${API}/reportings/create`, body);
    return res.data.reporting_id;
  };

  const handleManualUpload = async () => {
    if (!loantapeFile) {
      setError('Choose a loan tape file first.');
      return;
    }
    setUploading(true);
    setError(null);
    let id = null;
    try {
      id = await ensureReporting({ source: 'manual_upload' });
      await uploadFile(id, 'loantape', loantapeFile);
      if (cashflowsFile) await uploadFile(id, 'cashflows', cashflowsFile);
    } catch (err) {
      console.error('[handleManualUpload] error:', err.detail || errorMessage(err));
      setError(apiErrorDetail(err, 'Upload failed.'));
    } finally {
      setUploading(false);
      // Also after a partial failure, so the created reporting isn't lost.
      if (id) await onReportingCreated(id);
    }
  };

  const handleDbFetch = async () => {
    if (!loanbookLastDate) {
      setError('Choose a loan book last date first.');
      return;
    }
    setError(null);
    let id = null;
    try {
      id = await ensureReporting({});
      if (id !== reportingId) await onReportingCreated(id);
      await runJob(id, 'db_fetch', { loanbook_last_date: dateToIso(loanbookLastDate) });
      await onReportingCreated(id);
    } catch (err) {
      if (err.cancelled) return;
      console.error('[handleDbFetch] error:', err.detail || errorMessage(err));
      setError(apiErrorDetail(err, 'Database fetch failed.'));
    }
  };

  const handleValidate = async () => {
    if (!reportingId) return;
    setValidateError(null);
    try {
      await runJob(reportingId, 'validate', {});
      if (onValidated) await onValidated();
    } catch (err) {
      if (err.cancelled) return;
      console.error('[handleValidate] error:', err.detail || errorMessage(err));
      setValidateError(apiErrorDetail(err, 'Validate Data failed.'));
    }
  };

  const validating = !!(activeJob && activeJob.action === 'validate');
  const fetching = !!(activeJob && activeJob.action === 'db_fetch');

  if (reportingId && hasLoantape) {
    const recapLoantape = (inputsRecap && inputsRecap.loantape) || (inputsSummary.loantape ? { filename: inputsSummary.loantape } : null);
    const recapCashflows = inputsRecap
      ? inputsRecap.cashflows
      : inputsSummary.cashflows
        ? { filename: inputsSummary.cashflows }
        : null;
    return (
      <div>
        <div className="ex-section-header">
          <h3 className="ex-section-title">Select Data</h3>
        </div>
        {recapLoantape ? (
          <div className="flex flex-wrap gap-3 mb-4">
            <FileInfoCard icon="pi-table" label="Loan Tape" file={recapLoantape} />
            {recapCashflows && <FileInfoCard icon="pi-wallet" label="CashFlows" file={recapCashflows} />}
          </div>
        ) : (
          <LoadingState label="Loading input recap" />
        )}
        <p className="cr-help-text">
          Replacing the LoanTape/CashFlows file itself isn't wired up yet - see Validation for the preflight result
          on this data.
        </p>

        <PlatformFilesCard reportingId={reportingId} setLoading={setLoading} onUploaded={onStatusChanged} />

        <div className="cr-card cr-action-bar mt-4">
          <Button
            label="Validate Data"
            icon="pi pi-check-square"
            className="p-button-lg"
            onClick={handleValidate}
            disabled={busy}
            loading={validating}
          />
          <div className="cr-action-bar-text">
            {validatedAt
              ? `Last validated ${new Date(validatedAt).toLocaleString()}.`
              : 'Runs the preflight and all data checks - the Validation tab unlocks once it has run.'}
          </div>
        </div>
        {validationStale && !validating && (
          <div className="cr-warning-text mt-3">
            <i className="pi pi-exclamation-triangle mr-2" />
            Inputs changed since the last validation — run Validate Data again.
          </div>
        )}
        {validateError && <div className="ex-inline-error mt-3">{validateError}</div>}
      </div>
    );
  }

  return (
    <div>
      <div className="ex-section-header">
        <h3 className="ex-section-title">Select Data</h3>
      </div>
      {error && <div className="ex-inline-error mb-3">{error}</div>}

      <div className="flex flex-wrap gap-4">
        <SectionCard icon="pi-upload" title="Upload manually">
          <p className="cr-help-text">Loan tape (required) and CashFlows (optional) - .xlsx, .xlsm, .csv or .parquet.</p>
          <div className="cr-form-row align-items-center mb-3">
            <FileUpload
              mode="basic"
              name="loantape"
              chooseLabel={loantapeFile ? loantapeFile.name : 'Choose Loan Tape'}
              chooseOptions={{ className: 'p-button-outlined' }}
              auto={false}
              accept=".xlsx,.xlsm,.csv,.parquet"
              onSelect={(e) => setLoantapeFile(e.files[0])}
              onClear={() => setLoantapeFile(null)}
            />
            <FileUpload
              mode="basic"
              name="cashflows"
              chooseLabel={cashflowsFile ? cashflowsFile.name : 'Choose Cashflows (optional)'}
              chooseOptions={{ className: 'p-button-secondary p-button-outlined' }}
              auto={false}
              accept=".xlsx,.xlsm,.csv,.parquet"
              onSelect={(e) => setCashflowsFile(e.files[0])}
              onClear={() => setCashflowsFile(null)}
            />
          </div>
          <Button
            label="Upload"
            icon="pi pi-cloud-upload"
            onClick={handleManualUpload}
            disabled={!loantapeFile || uploading || busy}
            loading={uploading}
          />
        </SectionCard>

        <SectionCard icon="pi-database" title="Fetch from database">
          <p className="cr-help-text">Fetch the input data from the database as of the chosen loan book last date.</p>
          <label className="cr-field-label" htmlFor="cr-loanbook-last-date">
            Loan book last date
          </label>
          <div className="cr-form-row align-items-center mb-3">
            <Calendar
              inputId="cr-loanbook-last-date"
              value={loanbookLastDate}
              onChange={(e) => setLoanbookLastDate(e.value)}
              dateFormat="dd/mm/yy"
              showIcon
              placeholder="Loan book last date"
            />
            <Button
              label="Fetch from database"
              icon="pi pi-download"
              onClick={handleDbFetch}
              disabled={!loanbookLastDate || uploading || busy}
              loading={fetching}
            />
          </div>
          {fetching && (
            <p className="cr-help-text m-0">
              Fetching - see the progress bar above. The recap and "Validate Data" appear once it finishes.
            </p>
          )}
        </SectionCard>
      </div>
    </div>
  );
};

export default SelectDataTab;
